import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, Injector, OnInit, Renderer2 } from '@angular/core';
import { OktaAuthStateService, OKTA_CONFIG } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { AuthFacadeService } from 'ngpd-merceros-authentication-fe-components';
import { Subscription } from 'rxjs';

import { LoggedUser } from './interfaces';
import environment from '../environments/environment';
import { AuthService } from './services/auth.service';
import { SubscriberUserTypesService } from './services/subscriber-user-types.service';
import { LoadingType } from './shared/components/preloader/preloader.component';

@Component({
    selector: 'polaris-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export default class AppComponent implements OnInit {
    private _userSubscription: Subscription;

    loading: boolean;

    loadingMessage = 'Loading...';

    loadingType: LoadingType = 'default';

    title = 'starter-app';

    appVersion = environment.appVersion;

    currentYear = new Date().getFullYear();

    oktaAuth: OktaAuth;

    apiUnreachable = false;

    apiError = false;

    isMenuOpen = false;

    relayStatePayload = null;

    /**
     *
     * @param {OktaAuthStateService} authStateService - service that provides the current state of the Okta authentication
     * @param {Injector} injector - service that provides the OktaAuth instance
     * @param {HttpClient} http - service for making HTTP requests
     * @param _renderer2
     * @param _document
     * @param themingService
     * @param _authFacade
     * @param authService
     * @param subscriberUserTypesService
     */
    constructor(
        public authStateService: OktaAuthStateService,
        public injector: Injector,
        private http: HttpClient,
        private _renderer2: Renderer2,
        @Inject(DOCUMENT) private _document: Document,
        private _authFacade: AuthFacadeService,
        private authService: AuthService,
        private subscriberUserTypesService: SubscriberUserTypesService
    ) {
        // the OktaAuth instance is not directly available through the constructor dependency injection. Use the injector instead
        this.oktaAuth = injector.get(OKTA_CONFIG).oktaAuth;
    }

    /**
     *
     */
    ngOnInit(): void {
        // if (environment.PENDO_ENABLE && environment.PENDO_APP_KEY_AIDA) {
        //     this.loadScript(environment.PENDO_APP_KEY_AIDA);
        // }

        this._userSubscription = this.authService.loggedUser.subscribe((user: LoggedUser) => {
            const isAuthenticated = !!user; // true if user exists, false if null
            if (user?.relayState) {
                this.relayStatePayload = this.decodeJwtPayload(user?.relayState);
                this.subscriberUserTypesService.setFromUser(this.relayStatePayload);
            }

            // Unauthenticated redirects are handled by AuthGuardMatch so /login-verify can finish first.
            if (environment.enableTaapIntegration) {
                this.loading = !isAuthenticated;
            }
        });
    }

    /**
     *
     */
    async login() {
        await this.oktaAuth.signInWithRedirect();
    }
    /**
     *
     */
    async logout() {
        const idToken = this.oktaAuth.getIdToken();
        await this.oktaAuth.tokenManager.clear();
        window.location.replace(
            `${
                environment.oktaConfig.issuer
            }/v1/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${encodeURIComponent(
                environment.oktaConfig.postLogoutUri
            )}`
        );
    }
    /**
     * Toggle Menu for Responsive Design
     */
    toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen;
    }

    /**
     *
     * @param pendo_key
     */
    private loadScript(pendo_key) {
        const script = this._renderer2.createElement('script');

        script.text = `(function(apiKey){
        (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
            v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
            o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
            y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
            z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
        })('${pendo_key}');`;
        this._renderer2.appendChild(this._document.head, script);
    }

    /**
     *
     */
    ngOnDestroy() {
        this._userSubscription.unsubscribe();
    }

    /**
     * Decodes JWT payload from relayState and returns null if the token is invalid.
     * @param {string | undefined} token - JWT token from relayState.
     * @returns {Record<string, unknown> | null} Decoded JWT payload or null when invalid.
     */
    private decodeJwtPayload(token?: string) {
        if (!token) {
            return null;
        }

        try {
            const base64Url = token.split('.')[1];
            if (!base64Url) {
                return null;
            }

            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                window
                    .atob(base64)
                    .split('')
                    .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                    .join('')
            );

            return JSON.parse(jsonPayload);
        } catch {
            return null;
        }
    }
}
